import React from 'react';
import TruncableText from './TruncableText';
import './styles.scss';

// Create sub-components for each text variant
const createTextVariant = (tag, variantClassName) => {
    return React.forwardRef(({
        contrast = 10,
        weight = null,
        left,
        right,
        maxLines,
        grow,
        loading = false,
        animated = false,
        animatedCharDelay = 0.05,
        className: userClassName,
        children,
        value,
        style,
        ...props
    }, ref) => {
        const Component = tag;
        const contrastClass = `csf-${11 - contrast}`;
        const growClass = grow ? 'text-grow' : '';
        const loadingClass = loading ? 'text-component--loading' : '';
        let combinedClassName = `text-component ${contrastClass} ${growClass} ${loadingClass}`.trim();

        const finalStyle = weight ? { ...style, fontWeight: weight } : style;

        // Determine gap size based on tag
        const isLargeHeading = ['h1', 'h2', 'h3'].includes(tag);
        const gap = isLargeHeading ? 'var(--gap-2)' : 'var(--gap-1)';

        // Clone left/right components to apply text-icon class
        const cloneWithSize = (element) => {
            if (!element) return null;

            // If it's a valid React element, clone it with text-icon class
            if (React.isValidElement(element)) {
                return React.cloneElement(element, {
                    className: `text-icon ${contrastClass} ${element.props.className || ''}`.trim(),
                    style: {
                        ...element.props.style
                    }
                });
            }

            return element;
        };

        const leftElement = cloneWithSize(left);
        const rightElement = cloneWithSize(right);

        // Create wrapper class with variant for icon sizing
        let wrapperClass = `text-wrapper text-wrapper-${tag} ${growClass}`.trim();

        if (left || right) {
            wrapperClass = `${wrapperClass} ${variantClassName} ${userClassName || ''}`.trim();
        } else {
            combinedClassName = `${combinedClassName} ${variantClassName} ${userClassName || ''}`.trim();
        }

        const resolveAnimatedString = () => {
            if (typeof children === 'string') return children;
            if (value != null && value !== '') return String(value);
            return null;
        };

        const renderBody = () => {
            if (animated) {
                const str = resolveAnimatedString();
                if (str != null) {
                    return str.split('').map((char, index) => (
                        <span
                            key={index}
                            className="text-animated-char"
                            style={{ animationDelay: `${index * animatedCharDelay}s` }}
                        >
                            {char}
                        </span>
                    ));
                }
            }
            return children || value;
        };

        // If maxLines is provided, use TruncableText (not combined with animated)
        if (maxLines && !animated) {
            const content = (
                <>
                    {leftElement}
                    <TruncableText
                        text={(typeof children === 'string' ? children : '') || value}
                        maxLines={maxLines}
                        component={Component}
                        className={combinedClassName}
                        style={finalStyle}
                        {...props}
                    />
                    {rightElement}
                </>
            );

            if (leftElement || rightElement) {
                return (
                    <div
                        ref={ref}
                        className={wrapperClass}
                        style={{ gap }}
                    >
                        {content}
                    </div>
                );
            }

            return content;
        }

        // Regular text without truncation (or per-character animation)
        const content = (
            <Component
                ref={ref}
                className={combinedClassName}
                style={finalStyle}
                {...props}
            >
                {renderBody()}
            </Component>
        );

        if (leftElement || rightElement) {
            return (
                <div
                    ref={ref}
                    className={wrapperClass}
                    style={{ gap }}
                >
                    {leftElement}
                    {content}
                    {rightElement}
                </div>
            );
        }

        return content;
    });
};

// Create the main Text object with sub-components
const Text = {
    h1: createTextVariant('h1', 'text-h1'),
    h2: createTextVariant('h2', 'text-h2'),
    h3: createTextVariant('h3', 'text-h3'),
    h4: createTextVariant('h4', 'text-h4'),
    h5: createTextVariant('h5', 'text-h5'),
    h6: createTextVariant('h6', 'text-h6'),
    p: createTextVariant('p', 'text-p'),
    span: createTextVariant('span', 'text-span'),
    small: createTextVariant('small', 'text-small'),
};

export default Text;